import { forwardRef, type HTMLAttributes } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";

export type ProgressBarSize = "sm" | "md" | "lg";

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Current progress value (0-100) */
  value: number;
  /** Maximum value (defaults to 100) */
  max?: number;
  /** Size of the progress bar */
  size?: ProgressBarSize;
  /** Discipline theming */
  discipline?: DisciplineSlug;
  /** Whether to show the percentage label */
  showLabel?: boolean;
  /** Whether to animate the progress fill */
  animated?: boolean;
  /** Accessible label for screen readers */
  "aria-label"?: string;
}

/**
 * Visual progress indicator with playful styling.
 * Supports discipline-themed colors and optional animated fills.
 *
 * @example
 * // Basic progress bar
 * <ProgressBar value={75} />
 *
 * @example
 * // Discipline-themed progress bar with label
 * <ProgressBar
 *   value={3}
 *   max={10}
 *   discipline="math"
 *   showLabel
 *   animated
 * />
 */
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      max = 100,
      size = "md",
      discipline,
      showLabel = false,
      animated = true,
      className = "",
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    // Calculate percentage
    const percentage = Math.min(100, Math.max(0, (value / max) * 100));

    // Size configurations
    const sizeStyles = {
      sm: {
        height: "h-3",
        radius: "rounded-full",
        label: "text-xs",
      },
      md: {
        height: "h-5",
        radius: "rounded-full",
        label: "text-sm",
      },
      lg: {
        height: "h-8",
        radius: "rounded-full",
        label: "text-base font-semibold",
      },
    };

    // Discipline gradient colors
    const disciplineGradients: Record<DisciplineSlug, string> = {
      math: "from-luna-light via-luna to-magic-400",
      spelling: "from-petal-light via-petal to-meadow-400",
      patterns: "from-prism-red via-prism-yellow to-prism-blue",
      typing: "from-whiskers-light via-whiskers to-sky-600",
      programming: "from-pixel-light via-pixel to-pixel-darker",
    };

    const currentSize = sizeStyles[size];
    const gradientClass = discipline
      ? `bg-gradient-to-r ${disciplineGradients[discipline]}`
      : "bg-gradient-to-r from-sunshine-400 via-sunshine-500 to-meadow-400";

    return (
      <div
        ref={ref}
        className={`w-full ${className}`}
        {...props}
      >
        {/* Label above the bar */}
        {showLabel && (
          <div className="flex justify-between items-center mb-1.5">
            <span className={`font-body ${currentSize.label} text-neutral-600`}>
              Progress
            </span>
            <span className={`font-display ${currentSize.label} text-neutral-800`}>
              {Math.round(percentage)}%
            </span>
          </div>
        )}

        {/* Progress bar container */}
        <div
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={ariaLabel ?? `Progress: ${Math.round(percentage)}%`}
          className={`
            relative
            w-full
            ${currentSize.height}
            ${currentSize.radius}
            bg-neutral-200
            overflow-hidden
            shadow-inner
          `}
        >
          {/* Progress fill */}
          <div
            className={`
              absolute inset-y-0 left-0
              ${currentSize.radius}
              ${gradientClass}
              shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)]
              ${animated ? "transition-all duration-500 ease-out" : ""}
            `}
            style={{ width: `${percentage}%` }}
          >
            {/* Shine effect */}
            <div
              className={`
                absolute inset-0
                ${currentSize.radius}
                bg-gradient-to-b from-white/30 to-transparent
              `}
            />

            {/* Animated shimmer */}
            {animated && percentage > 0 && (
              <div
                className="
                  absolute inset-0
                  bg-gradient-to-r from-transparent via-white/20 to-transparent
                  animate-[shimmer_2s_infinite]
                "
              />
            )}
          </div>

          {/* Decorative dots for large size */}
          {size === "lg" && percentage >= 10 && (
            <div className="absolute inset-0 flex items-center justify-evenly px-2">
              {[...Array(Math.min(10, Math.floor(percentage / 10)))].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-white/40"
                  style={{
                    animationDelay: `${i * 100}ms`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Stars earned indicator (for challenge progress) */}
        {max <= 10 && (
          <div className="flex justify-between mt-2">
            {[...Array(max)].map((_, i) => (
              <div
                key={i}
                className={`
                  w-4 h-4
                  ${i < value ? "text-sunshine-500" : "text-neutral-300"}
                  transition-all duration-300
                  ${i < value ? "scale-100" : "scale-90"}
                `}
              >
                <StarIcon className="w-full h-full" filled={i < value} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

ProgressBar.displayName = "ProgressBar";

/** Small star icon for progress indicator */
function StarIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      />
    </svg>
  );
}

// Add shimmer animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-progress-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default ProgressBar;
